import { ROLES } from "containers/Permissions";
import { TiVoImage } from "hooks/tivoApps";

import { IMAGES, IMAGE_ENVS } from "../constants";
import { PROVIDER_FIELDS, VARIANT_FIELDS } from "./fields";
import {
  TiVoAppFormBaseTab,
  TiVoAppFormCountriesTab,
  TiVoAppFormCspSummaryTab,
  TiVoAppFormCspTab,
  TiVoAppFormLaunchConfigTab,
  TiVoAppFormMediaTab,
  TiVoAppFormPresentationTab,
  TiVoAppFormUriTransformTab,
  TiVoAppFormZoomConfigurationTab,
} from "./tabs";

const generateImageSchema = () => {
  const schema: Record<string, TiVoImage> = {};

  Object.values(IMAGE_ENVS).forEach((environment) => {
    Object.entries(IMAGES).forEach(([imageKey, imageType]) => {
      const fieldName = `assets.${environment}.${IMAGES[imageKey as keyof typeof IMAGES]}.url`;
      schema[fieldName] = {
        environment,
        imageType,
        providerId: "",
      };
    });
  });

  return schema;
};

export const IMAGE_SCHEMA: Record<string, TiVoImage> = generateImageSchema();

export const TEXTS = {
  SHARED: {
    DISCARD_BUTTON_LABEL: "Discard changes",
    RESUBMIT_MESSAGE: "Please resubmit the form.",
    SAVE_BUTTON_LABEL: "Submit",
    SUBMIT_SUCCESS_MESSAGE: "The values have been saved.",
    VALIDATION_ERROR:
      "In the form, some values are missing or invalid. Please correct them and try again.",
  },
  DRAFT: {
    MODAL_TITLE: "Discard draft?",
    MODAL_DESC:
      "Are you sure that you want to discard your draft? This will not affect your already submitted Variant",
    KEEP_DRAFT: "Keep draft",
    REMOVE_DRAFT: "Remove draft",
  },
  VARIANT_VERSION_CONFLICT: {
    TITLE: "Duplicate playback instructions",
    DESCRIPTION:
      "Another active or submitted variant version has identical playback instructions. Consider making the changes to the matching version instead.",
    MATCHING_VARIANTS: " Matching variant labels: ",
    CONFIRM: "Create anyway",
    CANCEL: "Cancel",
  },
  BASE: {
    NAME: "App details",
    APP_LABEL_NOT_UNIQUE_ERROR:
      "A provider with this app label already exists.",
    ALLOW_LIST_HEADER: "Allow list",
    BEGIN_HEADER: "App info",
    HEADER: "General information about the application",
    ID_HEADER: "Internal IDs",
    PROVIDER_ID_NOT_UNIQUE_ERROR: "A provider with this ID already exists.",
    REG_EXP_PLACEHOLDER: "Regular expression",
    WELCOME_DESCRIPTION:
      'Thank you for submitting to Vewd. Before you begin please read <a href="/guidelines" target="_blank" rel="noopener noreferrer">these notes</a>.',
    WELCOME_HEADER: "Welcome",
  },
  COUNTRIES: {
    NAME: "Countries",
    HEADER: "Approved countries",
    TOOLTIP:
      "Use standard 2-letter country codes. Multiple countries can be added with commas",
  },
  SUPPORT: {
    NAME: "Support info",
    HEADER: "Support details",
  },
  CSP_SUMMARY: {
    NAME: "Contract summary",
    AGREEMENTS_HEADER: "Contract summary",
    ASSIGNEES_HEADER: "Internal stakeholders",
    SUMMARY_HEADER: "Obligations summary",
  },
  LAUNCH_CONFIG: {
    NAME: "Launch configuration",
    ATTRIBUTES_HEADER: "Attributes",
    BASIC_HEADER: "Basic",
    KEY_PLACEHOLDER: "Key",
    NAME_PLACEHOLDER: "Name",
    TAB_LAUNCH_MODE_LABEL: "Launch mode",
    TAB_LAUNCH_TYPE_LABEL: "Launch type",
    TAB_NEW_LABEL: "New",
    TARGET_HEADER: "Target",
    VALUE_PLACEHOLDER: "Value",
  },
  MEDIA: {
    NAME: "Images",
    POSTER_LOGO_HEADER: "Poster logo (2:3)",
    RECT_LOGO_HEADER: "Rectangular logo (16:9)",
    SQUARE_LOGO_HEADER: "Square logo (1:1)",
    WIDE_LOGO_HEADER: "Wide logo (14:5)",
    PROD: "Production",
    STAGING: "Staging",
    COPY_BUTTON: "Copy all from staging",
  },
  PRESENTATION: {
    NAME: "Presentation",
    ADD_CONFIG_BUTTON_LABEL: "Add new configuration",
    CONFIGS_HEADER: "Configurations",
    DEFAULT_CONFIG_LABEL: "Default",
    DELETE_CONFIG_BUTTON_LABEL: "Delete configuration",
    INHERITED_PLACEHOLDER: "Inherited",
    NEW_CONFIG_LABEL: "New",
    PARTNER_PICKS_HEADER: "Partner picks",
    PLATFORM_SUPPORTED_APP_PLACEHOLDER: "App",
    PLATFORM_SUPPORTED_FEATURE_PLACEHOLDER: "Feature",
  },
  PROVIDER: {
    NAME: "Provider",
    HEADER: "Provider details",
  },
  URI_TRANSFORM: {
    NAME: "URI transform",
    HEADER: "URI transform",
    NAME_PLACEHOLDER: "Name",
    VALUE_PLACEHOLDER: "Value",
  },
  ZOOM_CONFIG: {
    NAME: "Zoom configuration",
    HEADER: "Zoom configuration",
  },
};

export const PROVIDER_TABS = [
  {
    path: "base",
    text: TEXTS.BASE.NAME,
    allowed: [ROLES.tivo.provider],
    dataTestId: "base-tab",
    fields: Object.values(PROVIDER_FIELDS.BASE).map(({ name }) => name),
    Component: TiVoAppFormBaseTab,
  },
  {
    path: "media",
    text: TEXTS.MEDIA.NAME,
    allowed: [ROLES.tivo.provider],
    dataTestId: "media-tab",
    fields: Object.values(PROVIDER_FIELDS.IMAGES).map(({ name }) => name),
    Component: TiVoAppFormMediaTab,
  },
  {
    path: "countries",
    text: TEXTS.COUNTRIES.NAME,
    allowed: [ROLES.tivo.provider],
    dataTestId: "countries-tab",
    fields: Object.values(PROVIDER_FIELDS.COUNTRIES).map(({ name }) => name),
    Component: TiVoAppFormCountriesTab,
  },
  {
    path: "support",
    text: TEXTS.SUPPORT.NAME,
    allowed: [ROLES.tivo.provider, ROLES.tivo.bizDev],
    dataTestId: "support-tab",
    fields: Object.values(PROVIDER_FIELDS.SUPPORT).map(({ name }) => name),
    Component: TiVoAppFormCspTab,
  },
  {
    path: "summary",
    text: TEXTS.CSP_SUMMARY.NAME,
    allowed: [ROLES.tivo.provider, ROLES.tivo.bizDev],
    dataTestId: "csp-summary-tab",
    fields: Object.values(PROVIDER_FIELDS.CSP_SUMMARY).map(({ name }) => name),
    Component: TiVoAppFormCspSummaryTab,
  },
  {
    path: "presentation",
    text: TEXTS.PRESENTATION.NAME,
    allowed: [ROLES.tivo.provider],
    dataTestId: "presentation-tab",
    fields: Object.values(PROVIDER_FIELDS.PRESENTATION).map(({ name }) => name),
    Component: TiVoAppFormPresentationTab,
  },
];

export const VARIANT_TABS = [
  {
    path: "launch",
    text: TEXTS.LAUNCH_CONFIG.NAME,
    allowed: [ROLES.tivo.provider],
    dataTestId: "launch-config-tab",
    fields: Object.values(VARIANT_FIELDS.LAUNCH_CONFIG).map(({ name }) => name),
    Component: TiVoAppFormLaunchConfigTab,
  },
  {
    path: "uri-transform",
    text: TEXTS.URI_TRANSFORM.NAME,
    allowed: [ROLES.tivo.provider],
    dataTestId: "uri-trasnform-tab",
    fields: Object.values(VARIANT_FIELDS.URI_TRANSFORM).map(({ name }) => name),
    Component: TiVoAppFormUriTransformTab,
  },
  {
    path: "countries",
    text: TEXTS.COUNTRIES.NAME,
    allowed: [ROLES.tivo.provider],
    dataTestId: "countries-tab",
    fields: Object.values(PROVIDER_FIELDS.COUNTRIES).map(({ name }) => name),
    Component: TiVoAppFormCountriesTab,
  },
  {
    path: "zoom-config",
    text: TEXTS.ZOOM_CONFIG.NAME,
    allowed: [ROLES.tivo.provider],
    dataTestId: "zoom-config-tab",
    fields: Object.values(VARIANT_FIELDS.ZOOM_CONFIG).map(({ name }) => name),
    Component: TiVoAppFormZoomConfigurationTab,
  },
];
